/* eslint-disable react/no-danger */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import SEO, { getSeoTitleFromMetaData } from '../components/SEO';
import { routePropTypes } from '../utils/routes';
import HeadingsTOC from '../components/HeadingsTOC/headingstoc';
import VersionPicker from '../components/VersionPicker/VersionPicker';
import LeftNavPane from '../components/LeftNavPane/leftnavpane';
import TableOfContents from '../components/TableOfContents/tableofcontents';
import HeaderFooterLayout from '../layouts/headerfooter';
import Helpful from '../components/Misc/helpful';
import { isCortexPath } from '../utils/cortexNavigation';

const MarkdownTemplate = (props) => {
    const { data, location } = props;
    const { markdownRemark: post, pageMetadata: metadata } = data;
    const { frontmatter, html, parent } = post;
    const { modifiedTime } = parent ?? {};
    const { title, seoTitle, seoDescription } = frontmatter;
    const inCortex = isCortexPath(metadata?.path);
    const [scrollPosition, setScrollPosition] = useState(0);
    const markdownRef = useRef(null);

    let helpfulNeeded = false;
    if (metadata?.path) {
        helpfulNeeded = metadata.path.split('/').length > 3;
    }

    useEffect(() => {
        const handleScroll = () => {
          if (markdownRef.current) {
            // Get the scroll position of the child element
            setScrollPosition(markdownRef.current.scrollTop);
          }
        };
    
        // Add the scroll event listener to the child element
        const element = markdownRef.current;
        if (element) {
            element.addEventListener('scroll', handleScroll);
        }
    
        // Clean up the event listener when the component unmounts or markdownRef changes
        return () => {
          if (element) {
            element.removeEventListener('scroll', handleScroll);
          }
        };
      }, []);

    return (
        <HeaderFooterLayout location={props.location} metadata={metadata} hideFooter={true} contentClassName={inCortex ? 'cortex-content' : ''}>
            <LeftNavPane currPath={props.location.pathname} className={inCortex ? 'left-nav-pane--cortex' : ''}>
                <TableOfContents key={inCortex ? metadata.path : 'docs'} metadata={metadata} />
            </LeftNavPane>
            <div className="markdown__parent">
                <SEO
                    title={seoTitle || getSeoTitleFromMetaData(metadata)}
                    // Gastsby will return null if field does not exist
                    description={seoDescription || undefined}
                >
                    {metadata?.hideFromNav && <meta name="robots" content="noindex, nofollow" />}
                </SEO>
                <div className="markdown__container" ref={markdownRef}>
                    <div className='markdown' data-cy='markdown'>
                        <VersionPicker metadata={metadata} />
                        <h1>{title}</h1>
                        <div dangerouslySetInnerHTML={{ __html: html }} />
                        {helpfulNeeded && <Helpful />}
                    </div>
                </div>
            </div>
            <div className='markdown__rightNav'>
                <HeadingsTOC key='headings' currPath={metadata.path} modifiedTime={modifiedTime} scrollPosition={scrollPosition}/>
            </div>
        </HeaderFooterLayout>
    );
};

export const pageQuery = graphql`
    query MetadataBySlug($slug: String!) {
        pageMetadata(path: { eq: $slug }) {
            ...NavMetadata
        }

        markdownRemark(fields: { slug: { eq: $slug } }) {
            html
            frontmatter {
                title
                order
                seoTitle
                seoDescription
            }
            parent {
                ... on File {
                    modifiedTime(formatString: "MMMM D, YYYY")
                }
            }
        }
    }
`;

MarkdownTemplate.propTypes = {
    location: routePropTypes.location.isRequired,
    data: PropTypes.shape({
        markdownRemark: PropTypes.shape({
            frontmatter: PropTypes.shape({
                title: PropTypes.string.isRequired,
                seoTitle: PropTypes.string,
                seoDescription: PropTypes.string,
            }).isRequired,
            html: PropTypes.string.isRequired,
            headings: PropTypes.array,
            parent: PropTypes.shape({
                modifiedTime: PropTypes.string,
            }),
        }).isRequired,
        pageMetadata: PropTypes.object,
    }).isRequired,
    pageContext: PropTypes.shape({
        slug: PropTypes.string,
    }).isRequired,
};

export default MarkdownTemplate;
